import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { PROJECTS } from "../../data/content";

const EASE = [0.16, 1, 0.3, 1];
const VIEWPORT = { once: true, margin: "-80px" };

const SHOWCASE_SLIDES = [
  { src: "/project-workcafe.jpg", label: "Corporate Work Café Fit-Out" },
  { src: "/project-cafeteria.jpg", label: "Cafeteria & Breakout Interiors" },
  { src: "/project-reactor.jpg", label: "Process Equipment Installation" },
  { src: "/project-labfurniture.jpg", label: "Laboratory Furniture & Fit-Out" },
  { src: "/design-interior-lab.jpg", label: "Interiors & Laboratory" },
  { src: "/design-cleanroom.jpg", label: "Cleanroom Design" },
  { src: "/design-piping.jpg", label: "Process & Utility Piping" },
  { src: "/design-hvac.jpg", label: "HVAC Engineering" },
  { src: "/design-cadbim.jpg", label: "CAD & BIM Modeling" },
];

const ProjectsShowcase = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % SHOWCASE_SLIDES.length), 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={VIEWPORT}
      transition={{ duration: 0.7, delay: 0.25, ease: EASE }}
      className="relative"
      data-testid="projects-showcase"
    >
      <span aria-hidden="true" className="absolute -left-2 -top-2 h-6 w-6 border-l-2 border-t-2 border-[#F47721]" />
      <span aria-hidden="true" className="absolute -right-2 -top-2 h-6 w-6 border-r-2 border-t-2 border-[#F47721]" />
      <span aria-hidden="true" className="absolute -bottom-2 -left-2 h-6 w-6 border-b-2 border-l-2 border-[#F47721]" />
      <span aria-hidden="true" className="absolute -bottom-2 -right-2 h-6 w-6 border-b-2 border-r-2 border-[#F47721]" />

      <div className="relative aspect-[16/10] overflow-hidden rounded-md border border-[#E5E8EA] bg-[#0E1B2A] shadow-[0_24px_60px_rgba(19,38,58,0.14)]">
        <AnimatePresence initial={false}>
          <motion.img
            key={index}
            src={SHOWCASE_SLIDES[index].src}
            alt={`${SHOWCASE_SLIDES[index].label} project execution visual`}
            data-testid="projects-showcase-slide"
            className="absolute inset-0 h-full w-full object-cover"
            initial={{ x: "16%", opacity: 0, scale: 1.05 }}
            animate={{ x: 0, opacity: 1, scale: 1 }}
            exit={{ x: "-16%", opacity: 0, scale: 1.03 }}
            transition={{ duration: 1.05, ease: EASE }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E1B2A]/70 via-transparent to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
          <AnimatePresence mode="wait">
            <motion.span
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="rounded-sm bg-[#0E1B2A]/85 px-3 py-1.5 font-manrope text-[10px] font-bold uppercase tracking-[0.2em] text-white backdrop-blur-sm"
              data-testid="projects-showcase-label"
            >
              {SHOWCASE_SLIDES[index].label}
            </motion.span>
          </AnimatePresence>
          <div className="flex items-center gap-1.5" data-testid="projects-showcase-dots">
            {SHOWCASE_SLIDES.map((slide, i) => (
              <span
                key={slide.src}
                aria-hidden="true"
                className={`h-1 rounded-full transition-[width,background-color] duration-500 ${i === index ? "w-6 bg-[#F47721]" : "w-2 bg-white/40"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ProjectsPattern = () => (
  <svg aria-hidden="true" data-testid="projects-pattern" className="w-full h-full text-[#24589A]" viewBox="0 0 1200 800" preserveAspectRatio="none">
    <defs>
      <pattern id="projects-cad-grid" width="72" height="72" patternUnits="userSpaceOnUse">
        <path d="M 72 0 L 0 0 0 72" fill="none" stroke="currentColor" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="1200" height="800" fill="url(#projects-cad-grid)" />
    <g fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M120 640 H360 V500 H560 V640 H820 V460 H1080" />
      <path d="M180 180 H430 V300 H680 V180 H980" />
      <circle cx="430" cy="300" r="36" />
      <circle cx="980" cy="180" r="46" />
    </g>
  </svg>
);

const ProjectCard = ({ project, index }) => (
  <motion.article
    data-testid={`project-card-${project.num}`}
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={VIEWPORT}
    transition={{ duration: 0.65, delay: 0.15 + index * 0.11, ease: EASE }}
    whileHover={{ y: -4 }}
    className="group relative h-full overflow-hidden rounded-[10px] border border-[#E5E8EA] bg-white/[0.78] p-7 md:p-8 shadow-[0_14px_40px_rgba(19,38,58,0.06)] backdrop-blur-sm transition-[background-color,border-color,box-shadow] duration-300 hover:border-[#F47721]/35 hover:bg-white hover:shadow-[0_20px_50px_rgba(19,38,58,0.09)]"
  >
    <span aria-hidden="true" className="absolute left-0 top-0 h-full w-[3px] origin-top scale-y-0 bg-[#F47721] transition-transform duration-300 group-hover:scale-y-100" />
    <div className="flex items-start justify-between gap-5">
      <div>
        <span className="font-manrope text-[44px] font-bold leading-none tracking-tight text-[#E2E8EE] transition-colors duration-300 group-hover:text-[#F47721]">
          {project.num}
        </span>
        <p className="font-inter text-[11px] font-semibold uppercase tracking-[0.22em] text-[#24589A] mt-4">{project.sector}</p>
      </div>
      <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E8EA] bg-[#F7F7F4] px-3 py-1.5 font-inter text-[10px] font-semibold uppercase tracking-[0.16em] text-[#17232D]">
        <CheckCircle2 size={13} className="text-[#F47721]" /> {project.status}
      </span>
    </div>

    <p className="font-inter text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F47721] mt-7">{project.client}</p>
    <h3 className="font-manrope font-bold tracking-tight text-[#17232D] text-[24px] md:text-[27px] leading-[1.18] mt-3">
      {project.title}
    </h3>

    <div className="mt-7 border-t border-[#E5E8EA] pt-6">
      <p className="font-inter text-[10px] font-semibold uppercase tracking-[0.2em] text-[#59636D]">Scope</p>
      <div className="mt-3 flex flex-wrap gap-2" data-testid={`project-scope-${project.num}`}>
        {project.scope.map((item) => (
          <span key={item} className="rounded-full border border-[#E5E8EA] bg-[#F7F7F4] px-3 py-1.5 font-inter text-[10px] font-semibold uppercase tracking-[0.1em] text-[#17232D]">
            {item}
          </span>
        ))}
      </div>
    </div>

    <div className="mt-6">
      <p className="font-inter text-[10px] font-semibold uppercase tracking-[0.2em] text-[#59636D]">Execution Highlights</p>
      <ul className="mt-3 space-y-2.5" data-testid={`project-highlights-${project.num}`}>
        {project.highlights.map((item) => (
          <li key={item} className="font-inter text-[14px] leading-relaxed text-[#59636D] flex items-start gap-2.5">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-[#F47721]" />
            {item}
          </li>
        ))}
      </ul>
    </div>

    <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-2 border-t border-[#E5E8EA] pt-5" data-testid={`project-outcomes-${project.num}`}>
      {project.metrics.map((metric) => (
        <div key={metric} className="border-l-0 sm:border-l sm:border-[#E5E8EA] sm:pl-3 sm:first:border-l-0 sm:first:pl-0">
          <p className="font-manrope text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.08em] sm:tracking-[0.1em] leading-snug text-[#13263A] break-words">{metric}</p>
        </div>
      ))}
    </div>
  </motion.article>
);

export const Projects = () => (
  <section id="projects" data-testid="projects-section" className="relative overflow-hidden bg-[#F7F7F4] py-24 lg:py-32">
    <motion.div
      aria-hidden="true"
      className="absolute inset-[-8%] opacity-[0.04] pointer-events-none"
      animate={{ x: [-22, 22, -22], y: [-8, 8, -8] }}
      transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
    >
      <ProjectsPattern />
    </motion.div>

    <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEWPORT}
          transition={{ duration: 0.6, ease: EASE }}
          className="max-w-4xl"
        >
          <div className="flex items-center gap-4">
            <span className="h-[2px] w-10 bg-[#F47721]" />
            <p className="font-inter text-[11px] font-semibold uppercase tracking-[0.32em] text-[#F47721]" data-testid="projects-label">
              Projects Delivered
            </p>
          </div>
          <h2 className="font-manrope font-bold tracking-tight leading-[1.06] text-[42px] md:text-[52px] lg:text-[56px] mt-6" data-testid="projects-heading">
            <span className="block text-[#13263A]">PROJECT EXECUTION.</span>
            <span className="block text-[#F47721]">PROVEN THROUGH DELIVERY.</span>
          </h2>
          <p className="font-inter text-[#59636D] text-base md:text-[17px] leading-[1.65] mt-6 max-w-2xl" data-testid="projects-description">
            Representative case studies presented without confidential site imagery — structured around scope, execution highlights and delivery outcomes.
          </p>
        </motion.div>

        <ProjectsShowcase />
      </div>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="projects-grid">
        {PROJECTS.map((project, i) => (
          <ProjectCard key={project.num} project={project} index={i} />
        ))}
      </div>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={VIEWPORT}
        transition={{ duration: 0.55, delay: 0.15, ease: EASE }}
        className="font-inter text-[#59636D] text-sm leading-[1.7] mt-10 max-w-3xl"
        data-testid="projects-confidentiality-note"
      >
        Client references are shown from the company profile. Detailed project photographs and sensitive technical information can be shared directly where appropriate.
      </motion.p>
    </div>
  </section>
);
